import React, { useState, useEffect, useRef } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  fetchStateData,
  fetchDistrictData,
  fetchCityData,
  BASE_URL,
} from "../Services/APIService";

interface CreateLocationProps {
  onCreateComplete: () => void;
  onCancel: () => void;
}

interface State {
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface District {
  districtId: number;
  districtName: string;
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface City {
  cityId: number;
  cityName: string;
  districtId: number;
  districtName: string;
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface FormData {
  locationName: string;
  stateId: number | null;
  districtId: number | null;
  cityId: number | null;
  street: string;
  zipcode: string;
  locality: string;
  latitude: string;
  longitude: string;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  address: {
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
    postcode?: string;
    suburb?: string;
    neighbourhood?: string;
    city_district?: string;
    state_district?: string;
    county?: string;
  };
}

const CreateLocationForm: React.FC<CreateLocationProps> = ({
  onCreateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    locationName: "",
    stateId: null,
    districtId: null,
    cityId: null,
    street: "",
    zipcode: "",
    locality: "",
    latitude: "",
    longitude: "",
  });

  const [states, setStates] = useState<State[]>([]);
  const [allDistricts, setAllDistricts] = useState<District[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<District[]>([]);
  const [allCities, setAllCities] = useState<City[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch states and districts on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingStates(true);
      setIsLoadingDistricts(true);

      try {
        // Fetch states
        const stateData = await fetchStateData();
        const activeStates = stateData.filter(
          (state) => state.isActive === true
        );
        setStates(activeStates);

        // Fetch all districts using fetchDistrictData from APIService
        const districtData = await fetchDistrictData();
        const activeDistricts = districtData.filter(
          (district) => district.isActive === true
        );
        setAllDistricts(activeDistricts);

        // Fetch all cities
        setIsLoadingCities(true);
        const cityData = await fetchCityData();
        const activeCities = cityData.filter((city) => city.isActive === true);
        setAllCities(activeCities);
      } catch (error) {
        setError("Failed to fetch data");
        console.error(error);
      } finally {
        setIsLoadingStates(false);
        setIsLoadingDistricts(false);
        setIsLoadingCities(false);
      }
    };

    fetchData();

    // Handle click outside search results
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchResultsRef.current &&
        searchInputRef.current &&
        !searchResultsRef.current.contains(event.target as Node) &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Filter districts when state is selected or district data changes
  useEffect(() => {
    if (formData.stateId && allDistricts.length > 0) {
      const districts = allDistricts.filter(
        (district) => district.stateId === formData.stateId
      );

      setFilteredDistricts(districts);

      // Clear district and city selections when state changes
      if (
        formData.districtId &&
        !districts.some((d) => d.districtId === formData.districtId)
      ) {
        setFormData((prev) => ({
          ...prev,
          districtId: null,
          cityId: null,
        }));
      }
    } else {
      setFilteredDistricts([]);
      setFormData((prev) => ({
        ...prev,
        districtId: null,
        cityId: null,
      }));
    }
  }, [formData.stateId, allDistricts]);

  // Filter cities when district is selected or city data changes
  useEffect(() => {
    if (formData.districtId && allCities.length > 0) {
      const cities = allCities.filter(
        (city) => city.districtId === formData.districtId
      );

      setFilteredCities(cities);

      // Clear city selection when district changes
      if (
        formData.cityId &&
        !cities.some((c) => c.cityId === formData.cityId)
      ) {
        setFormData((prev) => ({
          ...prev,
          cityId: null,
        }));
      }
    } else {
      setFilteredCities([]);
      setFormData((prev) => ({
        ...prev,
        cityId: null,
      }));
    }
  }, [formData.districtId, allCities]);

  // Fetch cities
  const fetchCities = async () => {
    setIsLoadingCities(true);
    const authToken = localStorage.getItem("authToken");

    if (!authToken) {
      throw new Error("Authentication token not found");
    }

    try {
      const response = await fetch(`${BASE_URL}/Location/GetAllCities`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.cityList) {
        setAllCities(data.cityList);
      } else {
        throw new Error("Invalid city data received");
      }
    } catch (error) {
      setError("Failed to fetch cities");
      console.error(error);
    } finally {
      setIsLoadingCities(false);
    }
  };

  // Create Location
  const createLocation = async (locationData: any) => {
    const authToken = localStorage.getItem("authToken");

    if (!authToken) {
      throw new Error("Authentication token not found");
    }

    const response = await fetch(`${BASE_URL}/Location/CreateLocation`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(locationData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  };

  const handleLocationSearch = (query: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setShowResults(true);

    searchTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
            query
          )}&format=json&addressdetails=1&limit=5`,
          {
            headers: {
              "Accept-Language": "en",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data: NominatimResult[] = await response.json();
        console.log("Search Map API results:", data);
        setSearchResults(data);
      } catch (error) {
        console.error("Error fetching search results:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);
  };

  const handleSelectLocation = (place: NominatimResult) => {
    const getFirstWord = (text: string): string => {
      return text.split(/\s+/)[0];
    };

    // Get city name from various possible fields
    const rawCityName =
      place.address.city ||
      place.address.town ||
      place.address.village ||
      place.address.county ||
      place.address.city_district ||
      "";

    // Extract only the first word for city name
    const cityName = rawCityName ? getFirstWord(rawCityName) : "";

    const stateName = place.address.state || "";
    const streetName = place.address.road || "";
    const localityName =
      place.address.suburb || place.address.neighbourhood || "";
    const zipCode = place.address.postcode || "";
    const districtName =
      place.address.state_district || place.address.city_district || "";

    console.log("Selected Place:", place);
    console.log("City Name:", cityName);
    console.log("State Name:", stateName);
    console.log("District Name:", districtName);

    // Find matching state in our data
    const matchedState = states.find(
      (state) => state.stateName.toLowerCase() === stateName.toLowerCase()
    );

    const matchDistrict = allDistricts.find(
      (district) =>
        district.districtName.toLowerCase() === districtName.toLowerCase()
    );

    const matchCity = allCities.find(
      (city) => city.cityName.toLowerCase() === cityName.toLowerCase()
    );

    console.log("Matched State:", matchedState);
    console.log("Matched District:", matchDistrict);
    console.log("Matched City:", matchCity);

    // Update form data with location details
    setFormData({
      locationName: place.display_name,
      stateId: matchedState?.stateId || null,
      districtId: matchDistrict?.districtId || null,
      cityId: matchCity?.cityId || null,
      street: streetName,
      zipcode: zipCode,
      locality: localityName,
      latitude: place.lat,
      longitude: place.lon,
    });

    setShowResults(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "locationName") {
      handleLocationSearch(value);
    }

    if (name === "stateId") {
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value),
        districtId: null,
        cityId: null,
      }));
    } else if (name === "districtId" || name === "cityId") {
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.locationName.trim()) {
      setError("Please enter a valid Location Name");
      return;
    }

    if (!formData.stateId) {
      setError("Please select a State");
      return;
    }

    if (!formData.districtId) {
      setError("Please select a District");
      return;
    }

    if (!formData.cityId) {
      setError("Please select a City");
      return;
    }

    try {
      setIsLoading(true);

      // Get user ID and tenant ID from localStorage
      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      if (!userId || !tenantId) {
        throw new Error("User ID or Tenant ID not found in localStorage");
      }

      // Prepare request payload
      const payload = {
        locationName: formData.locationName,
        stateID: formData.stateId,
        districtID: formData.districtId,
        cityID: formData.cityId,
        street: formData.street,
        zipcode: formData.zipcode,
        locality: formData.locality,
        latitude: formData.latitude,
        longitude: formData.longitude,
        tenantID: parseInt(tenantId),
        createdBy: parseInt(userId),
      };

      const result = await createLocation(payload);

      if (result.isCreated) {
        toast.success(result.message || "Location Created Successfully!");
        // Reset form after successful submission
        setFormData({
          locationName: "",
          stateId: null,
          districtId: null,
          cityId: null,
          street: "",
          zipcode: "",
          locality: "",
          latitude: "",
          longitude: "",
        });
        // Call the refresh function
        onCreateComplete();
      } else {
        throw new Error(result.message || "Failed to create location");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  // Keyboard navigation for search results
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || searchResults.length === 0) return;

    const resultItems = document.querySelectorAll(".result-item");
    const selectedItem = document.querySelector(".result-item.selected");
    const selectedIndex = Array.from(resultItems).indexOf(
      selectedItem as Element
    );

    // Down arrow
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex =
        selectedIndex < resultItems.length - 1 ? selectedIndex + 1 : 0;
      resultItems.forEach((item, i) => {
        item.classList.toggle("selected", i === nextIndex);
      });
    }
    // Up arrow
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex =
        selectedIndex > 0 ? selectedIndex - 1 : resultItems.length - 1;
      resultItems.forEach((item, i) => {
        item.classList.toggle("selected", i === prevIndex);
      });
    }
    // Enter key
    else if (e.key === "Enter" && selectedItem) {
      e.preventDefault();
      const placeIndex = Array.from(resultItems).indexOf(selectedItem);
      if (placeIndex >= 0) {
        handleSelectLocation(searchResults[placeIndex]);
      }
    }
  };

  const handleCancel = () => {
    // Reset form data
    setFormData({
      locationName: "",
      stateId: null,
      districtId: null,
      cityId: null,
      street: "",
      zipcode: "",
      locality: "",
      latitude: "",
      longitude: "",
    });
    setError(null);
    // Call the cancel function
    onCancel();
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <div className="card mb-4">
      <div>
        <Toaster position="top-center" reverseOrder={false} />
      </div>
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Create Location</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}

          {/* Location Search */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="position-relative">
                <label htmlFor="locationName" className="form-label">
                  Location Name{requiredField}
                </label>
                <input
                  type="text"
                  id="locationName"
                  name="locationName"
                  className="form-control form-control-lg"
                  placeholder="Search for a location"
                  value={formData.locationName}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  ref={searchInputRef}
                  required
                />

                {/* Search Results */}
                {showResults && (
                  <div
                    ref={searchResultsRef}
                    className="position-absolute w-100 mt-1 shadow-sm"
                    style={{
                      zIndex: 1000,
                      maxHeight: "300px",
                      overflowY: "auto",
                      display: showResults ? "block" : "none",
                      backgroundColor: "#fff",
                      border: "1px solid rgb(222 226 230)",
                      borderRadius: "0.5rem",
                      top: "70px",
                    }}
                  >
                    {isSearching ? (
                      <div className="p-3 text-center text-muted">
                        Searching...
                      </div>
                    ) : searchResults.length === 0 ? (
                      <div className="p-3 text-center text-muted">
                        No results found
                      </div>
                    ) : (
                      searchResults.map((place, index) => (
                        <div
                          key={place.place_id}
                          className="result-item p-2 border-bottom"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSelectLocation(place)}
                        >
                          {place.display_name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            {/* State Selection */}
            <div className="col-12 col-lg-4 mb-3">
              <label htmlFor="stateId" className="form-label">
                State{requiredField}
              </label>
              {isLoadingStates ? (
                <div className="text-muted">Loading states...</div>
              ) : (
                <select
                  id="stateId"
                  name="stateId"
                  className="form-select form-control-lg"
                  value={formData.stateId ?? ""}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.stateId} value={state.stateId}>
                      {state.stateName}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* District Selection */}
            <div className="col-12 col-lg-4 mb-3">
              <label htmlFor="districtId" className="form-label">
                District{requiredField}
              </label>
              {isLoadingDistricts ? (
                <div className="text-muted">Loading districts...</div>
              ) : (
                <select
                  id="districtId"
                  name="districtId"
                  className="form-select form-control-lg"
                  value={formData.districtId ?? ""}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.stateId}
                >
                  <option value="">
                    {formData.stateId
                      ? filteredDistricts.length > 0
                        ? "Select District"
                        : "No districts available"
                      : "Select state first"}
                  </option>
                  {filteredDistricts.map((district) => (
                    <option
                      key={district.districtId}
                      value={district.districtId}
                    >
                      {district.districtName}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* City Selection */}
            <div className="col-12 col-lg-4 mb-3">
              <label htmlFor="cityId" className="form-label">
                City{requiredField}
              </label>
              {isLoadingCities ? (
                <div className="text-muted">Loading cities...</div>
              ) : (
                <select
                  id="cityId"
                  name="cityId"
                  className="form-select form-control-lg"
                  value={formData.cityId ?? ""}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.districtId}
                >
                  <option value="">
                    {formData.districtId
                      ? filteredCities.length > 0
                        ? "Select City"
                        : "No cities available"
                      : "Select district first"}
                  </option>
                  {filteredCities.map((city) => (
                    <option key={city.cityId} value={city.cityId}>
                      {city.cityName}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="row">
            {/* Street */}
            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="street" className="form-label">
                Street
              </label>
              <input
                type="text"
                id="street"
                name="street"
                className="form-control form-control-lg"
                placeholder="Enter Street"
                value={formData.street}
                onChange={handleInputChange}
              />
            </div>

            {/* Zipcode */}
            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="zipcode" className="form-label">
                Zipcode{requiredField}
              </label>
              <input
                type="text"
                id="zipcode"
                name="zipcode"
                className="form-control form-control-lg"
                placeholder="Enter Zipcode"
                value={formData.zipcode}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="row">
            {/* Locality */}
            <div className="col-12 col-lg-4 mb-3">
              <label htmlFor="locality" className="form-label">
                Locality
              </label>
              <input
                type="text"
                id="locality"
                name="locality"
                className="form-control form-control-lg"
                placeholder="Enter Locality"
                value={formData.locality}
                onChange={handleInputChange}
              />
            </div>

            {/* Latitude */}
            <div className="col-12 col-lg-4 mb-3">
              <label htmlFor="latitude" className="form-label">
                Latitude
              </label>
              <input
                type="text"
                id="latitude"
                name="latitude"
                className="form-control form-control-lg"
                placeholder="Enter Latitude"
                value={formData.latitude}
                onChange={handleInputChange}
              />
            </div>

            {/* Longitude */}
            <div className="col-12 col-lg-4 mb-3">
              <label htmlFor="longitude" className="form-label">
                Longitude
              </label>
              <input
                type="text"
                id="longitude"
                name="longitude"
                className="form-control form-control-lg"
                placeholder="Enter Longitude"
                value={formData.longitude}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-12 mt-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? "Creating..." : "Submit"}
              </button>
              <button
                type="button"
                className="btn btn-secondary mx-2"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLocationForm;
